import { useMemo, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Sun, PiggyBank, CalendarClock, Leaf, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/lenis";

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export const RoiCalculator = () => {
  const [bill, setBill] = useState(3000);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  const r = useMemo(() => {
    const unitsPerKw = 115;
    const rate = 8;
    const units = bill / rate;
    const kw = Math.max(1, Math.min(25, Math.ceil(units / unitsPerKw)));
    const cost = kw * 55000;
    const subsidy = kw >= 3 ? 78000 : kw === 2 ? 60000 : 30000;
    const annual = bill * 12 * 0.92;
    const payback = Math.max(1, (cost - subsidy) / annual);
    return {
      kw,
      subsidy,
      annual,
      payback,
      savings25: annual * 25,
      co2: kw * 1.2,
    };
  }, [bill]);

  const pct = ((bill - 1500) / (50000 - 1500)) * 100;

  const stats = [
    { icon: Sun, label: "Recommended system", value: `${r.kw} kW` },
    { icon: PiggyBank, label: "Govt subsidy", value: inr(r.subsidy) },
    { icon: CalendarClock, label: "Payback period", value: `${r.payback.toFixed(1)} yrs` },
    { icon: Leaf, label: "CO₂ offset / year", value: `${r.co2.toFixed(1)} t` },
  ];

  return (
    <section id="savings" className="relative py-24 lg:py-32" ref={ref}>
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="relative overflow-hidden rounded-[2rem] border border-gold/15 bg-surface p-7 sm:p-12 lg:p-14">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-gold/15 blur-[110px]"
          />
          <div className="relative grid gap-12 lg:grid-cols-2">
            <div>
              <p className="eyebrow">02 — Savings Calculator</p>
              <h2 className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.08]">
                What would <span className="text-gold-grad">your roof</span> earn?
              </h2>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-mutegray">
                Drag the slider to your average monthly electricity bill. We size your system,
                apply the PM Surya Ghar subsidy and show your real payback.
              </p>

              <div className="mt-10" data-testid="roi-slider-wrap">
                <div className="flex items-baseline justify-between">
                  <label htmlFor="bill-slider" className="font-mono text-[10px] tracking-[0.22em] uppercase text-mutegray">
                    Monthly electricity bill
                  </label>
                  <span className="font-display text-2xl font-bold text-gold" data-testid="roi-bill-value">
                    {inr(bill)}
                  </span>
                </div>
                <input
                  id="bill-slider"
                  type="range"
                  min="1500"
                  max="50000"
                  step="500"
                  value={bill}
                  data-testid="roi-bill-slider"
                  onChange={(e) => setBill(Number(e.target.value))}
                  className="gold-range mt-5"
                  style={{ "--fill": `${pct}%` }}
                />
                <div className="mt-3 flex justify-between font-mono text-[10px] text-mutegray">
                  <span>₹1,500</span>
                  <span>₹50,000</span>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10 rounded-2xl border border-gold/25 bg-gold/[0.06] p-6"
                data-testid="roi-25yr-savings"
              >
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold">
                  Estimated 25-year savings
                </p>
                <p className="mt-2 font-display text-4xl sm:text-5xl font-extrabold text-gold-grad">
                  {inr(r.savings25)}
                </p>
                <p className="mt-2 text-xs text-mutegray">
                  That's about {inr(r.annual)} every year — on a {r.kw} kW system.
                </p>
              </motion.div>
            </div>

            <div className="flex flex-col justify-center gap-4" data-testid="roi-stats">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center justify-between rounded-2xl border border-white/10 bg-ink/60 px-6 py-5 transition-colors duration-300 hover:border-gold/35"
                  data-testid={`roi-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <span className="flex items-center gap-3 text-sm text-warm/80">
                    <s.icon className="h-5 w-5 text-gold" />
                    {s.label}
                  </span>
                  <span className="font-display text-xl sm:text-2xl font-bold text-warm">{s.value}</span>
                </motion.div>
              ))}
              <button
                type="button"
                data-testid="roi-cta-quote"
                onClick={() => scrollToId("quote")}
                className="group mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-goldlight hover:shadow-[0_0_36px_rgba(245,158,11,0.4)]"
              >
                Lock in this estimate — free site survey
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoiCalculator;
